import { 
  Trophy, 
  Award, 
  Target, 
  Flame, 
  TrendingUp, 
  Heart,
  Droplets,
  Moon,
  BookOpen,
  Dumbbell,
  Calendar,
  Star,
  Zap,
  Crown
} from 'lucide-react';

// Achievement categories and definitions
export const ACHIEVEMENT_CATEGORIES = {
  STREAK: 'streak',
  MILESTONE: 'milestone',
  GOAL: 'goal',
  SPECIAL: 'special'
};

export const ACHIEVEMENTS = [
  // Streak Achievements
  {
    id: 'streak_3',
    name: 'Getting Started',
    description: 'Log entries for 3 consecutive days',
    icon: Flame,
    category: ACHIEVEMENT_CATEGORIES.STREAK,
    color: 'text-orange-600',
    bg: 'bg-orange-100',
    requirement: { type: 'streak', days: 3 }
  },
  {
    id: 'streak_7',
    name: 'One Week Warrior',
    description: 'Log entries for 7 consecutive days',
    icon: Calendar,
    category: ACHIEVEMENT_CATEGORIES.STREAK,
    color: 'text-blue-600',
    bg: 'bg-blue-100',
    requirement: { type: 'streak', days: 7 }
  },
  {
    id: 'streak_30',
    name: 'Monthly Master',
    description: 'Log entries for 30 consecutive days',
    icon: Crown,
    category: ACHIEVEMENT_CATEGORIES.STREAK,
    color: 'text-purple-600',
    bg: 'bg-purple-100',
    requirement: { type: 'streak', days: 30 }
  },
  {
    id: 'streak_100',
    name: 'Century Club',
    description: 'Log entries for 100 consecutive days',
    icon: Trophy,
    category: ACHIEVEMENT_CATEGORIES.STREAK,
    color: 'text-yellow-600',
    bg: 'bg-yellow-100',
    requirement: { type: 'streak', days: 100 }
  },

  // Milestone Achievements
  {
    id: 'steps_10k',
    name: 'Step Master',
    description: 'Walk 10,000 steps in a single day',
    icon: TrendingUp,
    category: ACHIEVEMENT_CATEGORIES.MILESTONE,
    color: 'text-green-600',
    bg: 'bg-green-100',
    requirement: { type: 'steps', count: 10000 }
  },
  {
    id: 'water_goal_7',
    name: 'Hydration Hero',
    description: 'Meet your water goal for 7 days',
    icon: Droplets,
    category: ACHIEVEMENT_CATEGORIES.MILESTONE,
    color: 'text-cyan-600',
    bg: 'bg-cyan-100',
    requirement: { type: 'water_streak', days: 7 }
  },
  {
    id: 'sleep_goal_7',
    name: 'Sleep Champion',
    description: 'Meet your sleep goal for 7 days',
    icon: Moon,
    category: ACHIEVEMENT_CATEGORIES.MILESTONE,
    color: 'text-indigo-600',
    bg: 'bg-indigo-100',
    requirement: { type: 'sleep_streak', days: 7 }
  },
  {
    id: 'journal_30',
    name: 'Reflective Mind',
    description: 'Write 30 journal entries',
    icon: BookOpen,
    category: ACHIEVEMENT_CATEGORIES.MILESTONE,
    color: 'text-purple-600',
    bg: 'bg-purple-100',
    requirement: { type: 'journal_count', count: 30 }
  },

  // Goal Achievements
  {
    id: 'weight_goal',
    name: 'Goal Crusher',
    description: 'Reach your weight goal',
    icon: Target,
    category: ACHIEVEMENT_CATEGORIES.GOAL,
    color: 'text-red-600',
    bg: 'bg-red-100',
    requirement: { type: 'weight_goal' }
  },
  {
    id: 'calorie_goal_30',
    name: 'Nutrition Pro',
    description: 'Stay within calorie goals for 30 days',
    icon: Award,
    category: ACHIEVEMENT_CATEGORIES.GOAL,
    color: 'text-amber-600',
    bg: 'bg-amber-100',
    requirement: { type: 'calorie_streak', days: 30 }
  },

  // Special Achievements
  {
    id: 'first_entry',
    name: 'First Steps',
    description: 'Log your first entry',
    icon: Star,
    category: ACHIEVEMENT_CATEGORIES.SPECIAL,
    color: 'text-yellow-600',
    bg: 'bg-yellow-100',
    requirement: { type: 'first_entry' }
  },
  {
    id: 'complete_profile',
    name: 'All Set Up',
    description: 'Complete your profile with all details',
    icon: Zap,
    category: ACHIEVEMENT_CATEGORIES.SPECIAL,
    color: 'text-blue-600',
    bg: 'bg-blue-100',
    requirement: { type: 'complete_profile' }
  },
  {
    id: 'heart_points_100',
    name: 'Heart Healthy',
    description: 'Earn 100 heart points in a week',
    icon: Heart,
    category: ACHIEVEMENT_CATEGORIES.SPECIAL,
    color: 'text-pink-600',
    bg: 'bg-pink-100',
    requirement: { type: 'heart_points', count: 100 }
  },
  {
    id: 'workout_warrior',
    name: 'Workout Warrior',
    description: 'Complete 50 workout sessions',
    icon: Dumbbell,
    category: ACHIEVEMENT_CATEGORIES.SPECIAL,
    color: 'text-orange-600',
    bg: 'bg-orange-100',
    requirement: { type: 'workout_count', count: 50 }
  }
];

// Get achievement by ID
export const getAchievementById = (id) => {
  return ACHIEVEMENTS.find(a => a.id === id);
};

// Get achievements by category
export const getAchievementsByCategory = (category) => {
  return ACHIEVEMENTS.filter(a => a.category === category);
};

// Check if user has unlocked an achievement
export const checkAchievement = (achievementId, userData) => {
  const achievement = getAchievementById(achievementId);
  if (!achievement) return false;

  const { requirement } = achievement;

  switch (requirement.type) {
    case 'streak':
      return userData.currentStreak >= requirement.days;
    
    case 'steps':
      return userData.maxSteps >= requirement.count;
    
    case 'water_streak':
      return userData.waterStreak >= requirement.days;
    
    case 'sleep_streak':
      return userData.sleepStreak >= requirement.days;
    
    case 'journal_count':
      return userData.journalCount >= requirement.count;
    
    case 'weight_goal':
      return userData.reachedWeightGoal === true;
    
    case 'calorie_streak':
      return userData.calorieStreak >= requirement.days;
    
    case 'first_entry':
      return userData.totalEntries > 0;
    
    case 'complete_profile':
      return userData.profileComplete === true;
    
    case 'heart_points':
      return userData.weeklyHeartPoints >= requirement.count;
    
    case 'workout_count':
      return userData.workoutCount >= requirement.count;
    
    default:
      return false;
  }
};

// Check all achievements and return newly unlocked ones
export const checkAllAchievements = (userData, unlockedAchievements = []) => {
  const newlyUnlocked = [];

  ACHIEVEMENTS.forEach(achievement => {
    const alreadyUnlocked = unlockedAchievements.includes(achievement.id);
    if (!alreadyUnlocked && checkAchievement(achievement.id, userData)) {
      newlyUnlocked.push(achievement);
    }
  });

  return newlyUnlocked;
};

// Calculate user stats for achievement checking
export const calculateAchievementStats = (allData) => {
  const {
    steps = [],
    water = [],
    sleep = [],
    journal = [],
    meals = [],
    weights = [],
    heartPoints = [],
    userSettings = {}
  } = allData;

  // Calculate current streak (any activity)
  const currentStreak = calculateStreak(allData);
  
  // Max steps in a day
  const maxSteps = Math.max(...steps.map(s => s.steps || 0), 0);
  
  // Water goal streak
  const waterStreak = calculateWaterStreak(water, userSettings.waterGoal || 2000);
  
  // Sleep goal streak
  const sleepStreak = calculateSleepStreak(sleep, userSettings.sleepGoal || 8);
  
  // Journal count
  const journalCount = journal.length;
  
  // Weight goal reached
  const reachedWeightGoal = checkWeightGoal(weights, userSettings);
  
  // Calorie streak
  const calorieStreak = calculateCalorieStreak(meals, userSettings.dailyCalorieGoal);
  
  // Total entries
  const totalEntries = steps.length + water.length + sleep.length + journal.length + meals.length;
  
  // Profile complete
  const profileComplete = Boolean(
    userSettings.age && 
    userSettings.height && 
    userSettings.weight && 
    userSettings.goal
  );
  
  // Weekly heart points
  const weeklyHeartPoints = calculateWeeklyHeartPoints(heartPoints);

  return {
    currentStreak,
    maxSteps,
    waterStreak,
    sleepStreak,
    journalCount,
    reachedWeightGoal,
    calorieStreak,
    totalEntries,
    profileComplete,
    weeklyHeartPoints,
    workoutCount: 0 // TODO: Implement when workout tracking is added
  };
};

// Helper: Calculate overall activity streak
const calculateStreak = (allData) => {
  const { steps, water, sleep, journal, meals } = allData;
  const allDates = new Set();
  
  [...steps, ...water, ...sleep, ...journal, ...meals].forEach(entry => {
    if (entry.dateId) allDates.add(entry.dateId);
  });
  
  const sortedDates = Array.from(allDates).sort().reverse();
  let streak = 0;
  const today = new Date();
  
  for (let i = 0; i < sortedDates.length; i++) {
    const expectedDate = new Date(today);
    expectedDate.setDate(today.getDate() - i);
    const expectedDateId = expectedDate.toISOString().split('T')[0];
    
    if (sortedDates[i] === expectedDateId) {
      streak++;
    } else {
      break;
    }
  }
  
  return streak;
};

// Helper: Calculate water goal streak
const calculateWaterStreak = (water, goal) => {
  const waterByDate = {};
  water.forEach(entry => {
    waterByDate[entry.dateId] = (waterByDate[entry.dateId] || 0) + entry.amount;
  });
  
  let streak = 0;
  const today = new Date();
  
  for (let i = 0; i < 365; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    const dateId = date.toISOString().split('T')[0];
    
    if (waterByDate[dateId] >= goal) {
      streak++;
    } else {
      break;
    }
  }
  
  return streak;
};

// Helper: Calculate sleep goal streak
const calculateSleepStreak = (sleep, goal) => {
  const sleepByDate = {};
  sleep.forEach(entry => {
    if (!sleepByDate[entry.dateId] || entry.duration > sleepByDate[entry.dateId]) {
      sleepByDate[entry.dateId] = entry.duration;
    }
  });
  
  let streak = 0;
  const today = new Date();
  
  for (let i = 0; i < 365; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    const dateId = date.toISOString().split('T')[0];
    
    if (sleepByDate[dateId] >= goal) {
      streak++;
    } else {
      break;
    }
  }
  
  return streak;
};

// Helper: Check weight goal
const checkWeightGoal = (weights, settings) => {
  if (!weights.length || !settings.targetWeight) return false;
  
  const latestWeight = weights[weights.length - 1].weight;
  const goal = settings.goal;
  
  if (goal === 'lose' && latestWeight <= settings.targetWeight) return true;
  if (goal === 'gain' && latestWeight >= settings.targetWeight) return true;
  
  return false;
};

// Helper: Calculate calorie goal streak
const calculateCalorieStreak = (meals, calorieGoal) => {
  if (!calorieGoal) return 0;
  
  const caloriesByDate = {};
  meals.forEach(meal => {
    caloriesByDate[meal.dateId] = (caloriesByDate[meal.dateId] || 0) + meal.calories;
  });
  
  let streak = 0;
  const today = new Date();
  
  for (let i = 0; i < 365; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    const dateId = date.toISOString().split('T')[0];
    
    const dailyCalories = caloriesByDate[dateId] || 0;
    const within = dailyCalories <= calorieGoal * 1.1 && dailyCalories >= calorieGoal * 0.9;
    
    if (within) {
      streak++;
    } else {
      break;
    }
  }
  
  return streak;
};

// Helper: Calculate weekly heart points
const calculateWeeklyHeartPoints = (heartPoints) => {
  const today = new Date();
  const weekAgo = new Date(today);
  weekAgo.setDate(today.getDate() - 7);
  const weekAgoId = weekAgo.toISOString().split('T')[0];
  
  return heartPoints
    .filter(entry => entry.dateId >= weekAgoId)
    .reduce((sum, entry) => sum + (entry.points || 0), 0);
};
